﻿import * as React from "react";

export const SITE_ORIGIN = "https://www.dinlabel.com";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/public/og-product.png`;

export const MARKETING_PATHS = new Set([
  "/",
  "/install",
  "/barcodes",
  "/buy",
  "/tutorials",
  "/faq",
  "/viewdistributors",
  "/privacy",
  "/eula",
]);

export type PageSeoOptions = {
  title: string;
  description?: string;
  canonicalPath?: string;
  imageUrl?: string;
  noindex?: boolean;
};

function upsertMeta(
  attr: "name" | "property",
  key: string,
  content: string | undefined,
) {
  if (typeof document === "undefined") {
    return;
  }
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertCanonical(href: string | undefined) {
  if (typeof document === "undefined") {
    return;
  }
  let el = document.head.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

export function absoluteUrl(path: string): string {
  if (!path || path === "/") {
    return `${SITE_ORIGIN}/`;
  }
  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Apply document title, description, canonical, and Open Graph / Twitter tags. */
export function applyPageSeo({
  title,
  description,
  canonicalPath,
  imageUrl = DEFAULT_OG_IMAGE,
  noindex = false,
}: PageSeoOptions) {
  if (typeof document === "undefined") {
    return;
  }

  document.title = title;

  upsertMeta("name", "description", description);
  upsertMeta(
    "name",
    "robots",
    noindex ? "noindex, nofollow" : "index, follow",
  );

  const canonical =
    canonicalPath != null ? absoluteUrl(canonicalPath) : undefined;
  upsertCanonical(canonical);

  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", "DinLabel");
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:image", imageUrl);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", imageUrl);
}

export function usePageSeo(options: PageSeoOptions) {
  React.useEffect(() => {
    applyPageSeo(options);
  }, [
    options.title,
    options.description,
    options.canonicalPath,
    options.imageUrl,
    options.noindex,
  ]);
}
